"use client";

import { useState, useEffect, useRef } from "react";
import {
  Copy,
  ExternalLink,
  Check,
  Heart,
  Wallet,
  QrCode,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  X,
  FileText,
  Eye,
  PenLine,
  Share2,
  Mail,
  Sun,
  Moon,
  BarChart3,
  Eye as EyeIcon,
  ChevronRight,
  Globe,
  Gift,
  CreditCard,
  Landmark,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { SiCashapp } from "react-icons/si";
import { useSelector } from "react-redux";
import { useWallet } from "@crossmint/client-sdk-react-ui";
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  addDoc,
  collection,
  query,
  orderBy,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import toast, { Toaster } from "react-hot-toast";
import QRCodeLib from "qrcode";
import QRCodeStyling from "qr-code-styling";
import moment from "moment";
import DonationBoxEditor, { type DonationBoxConfig } from "./DonationBoxEditor";
import InvoiceEditor, {
  type InvoiceConfig,
  computeInvoiceTotal,
} from "./InvoiceEditor";
import DonationPreview from "./DonationPreview";
import InvoicePreview from "./InvoicePreview";
import UniversalLinkPreview, {
  type UniversalLinkConfig,
  DEFAULT_UNIVERSAL_CONFIG,
} from "./UniversalLinkPreview";
import type { RootState } from "../store";
import {
  ALL_PAYMENT_METHOD_IDS,
  PAYMENT_METHOD_LABELS,
  HIDDEN_PAYMENT_METHOD_IDS,
  type PaymentMethodId,
} from "./PaymentFlow";

const DEFAULT_DONATION_CONFIG: DonationBoxConfig = {
  title: "",
  description: "",
  images: [],
  backgroundImage: null,
  primaryColor: "#EC4899",
  layout: "modern",
  imageLayout: "grid",
  thankYouMessage: "",
  buttonText: "",
};

const DEFAULT_INVOICE_CONFIG: InvoiceConfig = {
  logoURL: null,
  recipientEmail: "",
  amount: 0,
  invoiceNumber: "INV-001",
  dueDate: "",
  title: "",
  description: "",
  lineItems: [],
  currency: "USD",
  brandColor: "#6366f1",
  discountAmount: 0,
  terms: "",
  senderAddress: "",
};

const BASE_URL = window.location.origin;

// "New" badge on the CashApp Pay Link entry. Re-flagged on 2026-06-24 to run
// for the next 365 days (expires 2027-06-24), after which it hides itself —
// no follow-up cleanup needed. Bump this date to re-flag it as new.
const CASHAPP_NEW_UNTIL = new Date("2027-06-24T23:59:59Z");

// CashApp brand green + a center "$" badge for the CashApp Pay QR. Rendered as
// an inline SVG so it needs no asset file and stays crisp at any size. The
// badge is a green rounded square with a white "$".
const CASHAPP_GREEN = "#00D54B";
const CASHAPP_CENTER_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"><rect x="2" y="2" width="116" height="116" rx="28" fill="${CASHAPP_GREEN}"/><text x="60" y="86" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="80" font-weight="700" fill="#ffffff">$</text></svg>`,
  );

// Generate a CashApp-style QR: dotted modules, rounded "eyes", green brand
// colour, and a green $ badge in the middle (matches the CashApp look). Returns
// a PNG data URL so it drops straight into the existing <img src> modal.
async function generateCashAppQR(url: string): Promise<string> {
  const qr = new QRCodeStyling({
    width: 320,
    height: 320,
    type: "canvas",
    data: url,
    margin: 10,
    // High EC level so the centre badge never breaks scannability.
    qrOptions: { errorCorrectionLevel: "H" },
    backgroundOptions: { color: "#ffffff" },
    // Black dots on white, with the three big corner "eyes" in CashApp green.
    dotsOptions: { type: "dots", color: "#000000" },
    cornersSquareOptions: { type: "extra-rounded", color: CASHAPP_GREEN },
    cornersDotOptions: { type: "dot", color: CASHAPP_GREEN },
    image: CASHAPP_CENTER_LOGO,
    imageOptions: {
      margin: 6,
      imageSize: 0.26,
      hideBackgroundDots: true,
      crossOrigin: "anonymous",
    },
  });
  const blob = (await qr.getRawData("png")) as Blob | null;
  if (!blob) throw new Error("Failed to render CashApp QR");
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Generate the standard collect-link QR: CashTap-green dotted modules with the
// CashTap logo embedded in the centre. `hideBackgroundDots` clears a quiet zone
// around the logo so it sits cleanly on the QR's white field — no white badge —
// while the high EC level keeps it scannable. Returns a PNG data URL so it drops
// straight into the existing <img src> modal.
async function generateDottedQR(url: string): Promise<string> {
  const qr = new QRCodeStyling({
    width: 320,
    height: 320,
    type: "canvas",
    data: url,
    margin: 10,
    qrOptions: { errorCorrectionLevel: "H" },
    backgroundOptions: { color: "#ffffff" },
    dotsOptions: { type: "dots", color: "#61CE70" },
    cornersSquareOptions: { type: "extra-rounded", color: "#61CE70" },
    cornersDotOptions: { type: "dot", color: "#61CE70" },
    image: `${BASE_URL}/logo.png`,
    imageOptions: {
      margin: 6,
      imageSize: 0.28,
      hideBackgroundDots: true,
      crossOrigin: "anonymous",
    },
  });
  const blob = (await qr.getRawData("png")) as Blob | null;
  if (!blob) throw new Error("Failed to render QR");
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function Collect() {
  const { wallet } = useWallet();
  const user = useSelector((state: RootState) => state.user) as any;
  const walletAddress = (wallet?.address || user?.smartWallet || "").toLowerCase();

  // Slug state
  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState<string | null>(null);
  const [slugLoading, setSlugLoading] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [editingSlug, setEditingSlug] = useState(false);

  // Settings state
  const [collectSettings, setCollectSettings] = useState({
    requestPaymentEnabled: true,
    buyMeCoffeeEnabled: true,
    donationBoxEnabled: true,
    invoiceEnabled: true,
    paymentTheme: "dark" as "dark" | "light",
  });

  // Donation config state
  const [donationConfig, setDonationConfig] = useState<DonationBoxConfig>(
    DEFAULT_DONATION_CONFIG,
  );
  const [donationExpanded, setDonationExpanded] = useState(false);
  const [donationSaving, setDonationSaving] = useState(false);

  // Invoice config state
  const [invoiceConfig, setInvoiceConfig] = useState<InvoiceConfig>(
    DEFAULT_INVOICE_CONFIG,
  );
  const [invoiceExpanded, setInvoiceExpanded] = useState(false);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [invoiceSuccessModal, setInvoiceSuccessModal] = useState<{
    url: string;
    qrDataUrl: string | null;
  } | null>(null);

  // QR code state
  const [qrModals, setQrModals] = useState<Record<string, string | null>>({});
  // Original link URL per QR modal key, so the modal can show + copy it.
  const [qrLinks, setQrLinks] = useState<Record<string, string>>({});
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  // The owner's profile image lives on the Firestore user doc (the Redux
  // user slice doesn't carry photoURL), so we hydrate it from there.
  const [ownerPhotoURL, setOwnerPhotoURL] = useState<string | null>(null);

  // Share modal state
  const [shareModal, setShareModal] = useState<{ url: string; title: string } | null>(null);

  // Mobile preview tab
  const [mobileTab, setMobileTab] = useState<"editor" | "preview">("editor");

  // Analytics state
  const [pageViewCounts, setPageViewCounts] = useState<Record<string, number>>({
    request: 0,
    buymeCoffee: 0,
    donation: 0,
    invoice: 0,
  });
  const [recentViews, setRecentViews] = useState<Array<{ type: string; timestamp: Date }>>([]);
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);
  const [viewAllFilter, setViewAllFilter] = useState<string>("all");

  // Collapsible sections
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false);

  // Payment Link config state
  const [paymentLinkConfig, setPaymentLinkConfig] = useState<DonationBoxConfig>(
    DEFAULT_DONATION_CONFIG,
  );
  const [paymentLinkExpanded, setPaymentLinkExpanded] = useState(false);
  const [paymentLinkSaving, setPaymentLinkSaving] = useState(false);

  // Tips Jar config state
  const [coffeeConfig, setCoffeeConfig] = useState<DonationBoxConfig>(
    DEFAULT_DONATION_CONFIG,
  );
  const [coffeeExpanded, setCoffeeExpanded] = useState(false);
  const [coffeeSaving, setCoffeeSaving] = useState(false);

  // Universal Link config state
  const [universalConfig, setUniversalConfig] = useState<UniversalLinkConfig>(
    DEFAULT_UNIVERSAL_CONFIG,
  );
  const [universalSaving, setUniversalSaving] = useState(false);

  // Per-type disabled payment methods
  const [disabledMethodsByType, setDisabledMethodsByType] = useState<
    Record<string, string[]>
  >({
    request: [],
    coffee: [],
    donation: [],
    invoice: [],
  });

  // Active collect tab
  const [collectTab, setCollectTab] = useState<
    "universal" | "request" | "donation" | "coffee" | "invoice" | "cashapppay"
  >("universal");

  // Ref for auto-scrolling to content section
  const contentSectionRef = useRef<HTMLDivElement>(null);
  // Ref for the CashApp Pay Link hero card — the "CashApp Pay Link" menu item
  // scrolls here instead of to the editor content below.
  const cashappHeroRef = useRef<HTMLDivElement>(null);

  // Loading
  const [initialLoading, setInitialLoading] = useState(true);

  // Collect TX fee — applied platform-wide to every Collect payment
  // (individual + business accounts alike). Both percentage (stored as
  // a fraction, e.g. 0.029 = 2.9%) and the flat USDC fee per transaction
  // are admin-adjustable per user, with a global default fallback. We
  // display the resolved values together at the top of the page.
  const [collectFeePct, setCollectFeePct] = useState<number>(0.029);
  const [collectFeeFlat, setCollectFeeFlat] = useState<number>(0.30);

  const anyEditorOpen = donationExpanded || invoiceExpanded || paymentLinkExpanded || coffeeExpanded;

  // Fetch existing config
  useEffect(() => {
    const fetchConfig = async () => {
      if (!walletAddress) return;

      try {
        const userDoc = await getDoc(doc(db, "users", walletAddress));
        if (userDoc.exists()) {
          const data = userDoc.data();

          if (data.photoURL) {
            setOwnerPhotoURL(data.photoURL);
          }

          if (data.slug) {
            setSlug(data.slug);
            setSavedSlug(data.slug);
          }

          if (data.collectSettings) {
            setCollectSettings((prev) => ({ ...prev, ...data.collectSettings }));
          }

          // Resolve both displayed values: per-user override > global
          // default (config/feeSettings) > hard-coded fallback. Same
          // precedence as the backend's getSellerFeeContext.
          let cfgDoc: any = null;
          const loadCfg = async () => {
            if (cfgDoc !== null) return cfgDoc;
            try {
              const snap = await getDoc(doc(db, "config", "feeSettings"));
              cfgDoc = snap.exists() ? snap.data() : {};
            } catch {
              cfgDoc = {};
            }
            return cfgDoc;
          };

          if (typeof data.collectFeePct === "number") {
            setCollectFeePct(data.collectFeePct);
          } else {
            const cfg = await loadCfg();
            const def = cfg.collectFeePctDefault;
            if (typeof def === "number" && def >= 0 && def <= 0.10) {
              setCollectFeePct(def);
            }
          }

          if (typeof data.collectFeeFlat === "number") {
            setCollectFeeFlat(data.collectFeeFlat);
          } else {
            const cfg = await loadCfg();
            const def = cfg.collectFeeFlatDefault;
            if (typeof def === "number" && def >= 0 && def <= 5) {
              setCollectFeeFlat(def);
            }
          }

          if (data.donationBox) {
            setDonationConfig({ ...DEFAULT_DONATION_CONFIG, ...data.donationBox });
          }

          if (data.paymentLinkPage) {
            setPaymentLinkConfig({ ...DEFAULT_DONATION_CONFIG, ...data.paymentLinkPage });
          }

          if (data.coffeePage) {
            setCoffeeConfig({ ...DEFAULT_DONATION_CONFIG, ...data.coffeePage });
          }

          {
            // The Payment Link editor is one merged config: start from the
            // saved universal config (forced to "direct"), then overlay any
            // page-content fields that live on `paymentLinkPage`.
            const uni = {
              ...DEFAULT_UNIVERSAL_CONFIG,
              ...(data.universalLinkConfig || {}),
              mode: "direct" as const,
            };
            const pl = data.paymentLinkPage;
            if (pl) {
              if (pl.title) uni.title = pl.title;
              if (pl.description) uni.bio = pl.description;
              if (pl.buttonText) uni.buttonText = pl.buttonText;
              if (pl.primaryColor) uni.primaryColor = pl.primaryColor;
              if (typeof pl.minAmount === "number") uni.minAmount = pl.minAmount;
              if (typeof pl.maxAmount === "number") uni.maxAmount = pl.maxAmount;
            }
            setUniversalConfig(uni);
          }

          if (data.disabledMethodsByType) {
            setDisabledMethodsByType((prev) => ({ ...prev, ...data.disabledMethodsByType }));
          }
        }

        // Fetch page view analytics
        const viewsQuery = query(
          collection(db, "users", walletAddress, "pageViews"),
          orderBy("timestamp", "desc"),
        );
        const viewsSnap = await getDocs(viewsQuery);
        const counts: Record<string, number> = { request: 0, buymeCoffee: 0, donation: 0, invoice: 0 };
        const recent: Array<{ type: string; timestamp: Date }> = [];
        viewsSnap.forEach((d) => {
          const vd = d.data();
          const t = vd.type as string;
          if (t in counts) counts[t]++;
          if (recent.length < 50 && vd.timestamp) {
            recent.push({ type: t, timestamp: vd.timestamp.toDate() });
          }
        });
        setPageViewCounts(counts);
        setRecentViews(recent);
      } catch (err) {
        console.error("Error fetching collect config:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchConfig();
  }, [walletAddress]);

  // Slug validation
  const validateSlug = (value: string): string | null => {
    if (value.length < 3) return "Slug must be at least 3 characters";
    if (value.length > 30) return "Slug must be 30 characters or less";
    if (!/^[a-z0-9-]+$/.test(value))
      return "Only lowercase letters, numbers, and hyphens allowed";
    if (value.startsWith("-") || value.endsWith("-"))
      return "Cannot start or end with a hyphen";
    return null;
  };

  const handleSlugSave = async () => {
    const cleanSlug = slug.toLowerCase().trim();
    const error = validateSlug(cleanSlug);
    if (error) {
      setSlugError(error);
      return;
    }

    setSlugLoading(true);
    setSlugError(null);

    try {
      await runTransaction(db, async (transaction) => {
        const slugRef = doc(db, "slugs", cleanSlug);
        const slugDoc = await transaction.get(slugRef);

        if (
          slugDoc.exists() &&
          slugDoc.data().walletAddress !== walletAddress
        ) {
          throw new Error(
            "This slug is already taken. Please choose another.",
          );
        }

        // Delete old slug if changing
        if (savedSlug && savedSlug !== cleanSlug) {
          const oldSlugRef = doc(db, "slugs", savedSlug);
          transaction.delete(oldSlugRef);
        }

        // Set new slug
        transaction.set(slugRef, {
          walletAddress,
          createdAt: serverTimestamp(),
        });

        // Update user doc
        const userRef = doc(db, "users", walletAddress);
        transaction.update(userRef, { slug: cleanSlug });
      });

      setSavedSlug(cleanSlug);
      setEditingSlug(false);
      toast.success("Custom slug saved!");
    } catch (err: any) {
      console.error("Slug save error:", err);
      setSlugError(err.message || "Failed to save slug");
      toast.error(err.message || "Failed to save slug");
    } finally {
      setSlugLoading(false);
    }
  };

  const handleRemoveSlug = async () => {
    if (!savedSlug) return;

    setSlugLoading(true);
    try {
      await deleteDoc(doc(db, "slugs", savedSlug));
      await updateDoc(doc(db, "users", walletAddress), { slug: null });
      setSavedSlug(null);
      setSlug("");
      setEditingSlug(false);
      toast.success("Custom slug removed");
    } catch (err) {
      console.error("Slug remove error:", err);
      toast.error("Failed to remove slug");
    } finally {
      setSlugLoading(false);
    }
  };

  // Toggle settings
  const toggleSetting = async (key: keyof typeof collectSettings) => {
    const newSettings = {
      ...collectSettings,
      [key]: !collectSettings[key],
    };
    setCollectSettings(newSettings);

    try {
      await updateDoc(doc(db, "users", walletAddress), {
        collectSettings: newSettings,
      });
    } catch (err) {
      console.error("Settings update error:", err);
      toast.error("Failed to update setting");
      setCollectSettings(collectSettings);
    }
  };

  // Toggle a payment method for a specific type
  const togglePaymentMethod = async (
    type: string,
    methodId: string,
  ) => {
    const current = disabledMethodsByType[type] || [];
    const updated = current.includes(methodId)
      ? current.filter((id) => id !== methodId)
      : [...current, methodId];
    const newState = { ...disabledMethodsByType, [type]: updated };
    setDisabledMethodsByType(newState);

    try {
      await updateDoc(doc(db, "users", walletAddress), {
        disabledMethodsByType: newState,
      });
    } catch (err) {
      console.error("Failed to save payment methods:", err);
      toast.error("Failed to update payment methods");
      setDisabledMethodsByType(disabledMethodsByType);
    }
  };

  // Save donation config
  const handleDonationSave = async () => {
    setDonationSaving(true);
    try {
      await updateDoc(doc(db, "users", walletAddress), {
        donationBox: donationConfig,
      });
      toast.success("Donation page saved!");
    } catch (err) {
      console.error("Donation save error:", err);
      toast.error("Failed to save donation page");
    } finally {
      setDonationSaving(false);
    }
  };

  // Save payment link config
  const handlePaymentLinkSave = async () => {
    setPaymentLinkSaving(true);
    try {
      await updateDoc(doc(db, "users", walletAddress), {
        paymentLinkPage: paymentLinkConfig,
      });
      toast.success("Payment link page saved!");
    } catch (err) {
      console.error("Payment link save error:", err);
      toast.error("Failed to save payment link page");
    } finally {
      setPaymentLinkSaving(false);
    }
  };

  // Save tips jar config
  const handleCoffeeSave = async () => {
    setCoffeeSaving(true);
    try {
      await updateDoc(doc(db, "users", walletAddress), {
        coffeePage: coffeeConfig,
      });
      toast.success("Tips Jar page saved!");
    } catch (err) {
      console.error("Coffee save error:", err);
      toast.error("Failed to save Tips Jar page");
    } finally {
      setCoffeeSaving(false);
    }
  };

  // Save the merged Payment Link config. The single link always opens the
  // minimal, image-free payment card (mode "direct"). The page-content fields
  // are mirrored into `paymentLinkPage` because that's what the public
  // /pay/:slug/payment route renders.
  const handleUniversalSave = async () => {
    setUniversalSaving(true);
    try {
      await updateDoc(doc(db, "users", walletAddress), {
        universalLinkConfig: {
          ...universalConfig,
          mode: "direct",
          backgroundImage: null,
        },
        paymentLinkPage: {
          ...paymentLinkConfig,
          title: universalConfig.title,
          description: universalConfig.bio,
          primaryColor: universalConfig.primaryColor,
          buttonText: universalConfig.buttonText,
          minAmount: universalConfig.minAmount,
          maxAmount: universalConfig.maxAmount,
          images: [],
          backgroundImage: null,
          layout: "minimal",
        },
      });
      toast.success("Payment link saved!");
    } catch (err) {
      console.error("Payment link save error:", err);
      toast.error("Failed to save payment link");
    } finally {
      setUniversalSaving(false);
    }
  };

  // Generate unique invoice number
  const generateInvoiceNumber = () => {
    const now = new Date();
    const y = now.getFullYear().toString().slice(-2);
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `INV-${y}${m}${d}-${rand}`;
  };

  // Save invoice
  const handleInvoiceSave = async () => {
    setInvoiceSaving(true);
    try {
      const total = computeInvoiceTotal(invoiceConfig);
      const autoInvoiceNumber = invoiceId
        ? invoiceConfig.invoiceNumber
        : generateInvoiceNumber();

      const docData = {
        ownerWallet: walletAddress,
        ownerUsername: user?.username || "",
        ownerLogoURL: invoiceConfig.logoURL,
        recipientEmail: invoiceConfig.recipientEmail,
        amount: total,
        currency: "USD",
        invoiceNumber: autoInvoiceNumber,
        dueDate: invoiceConfig.dueDate,
        title: invoiceConfig.title,
        description: invoiceConfig.description,
        lineItems: invoiceConfig.lineItems,
        brandColor: invoiceConfig.brandColor,
        discountAmount: invoiceConfig.discountAmount,
        terms: invoiceConfig.terms,
        senderAddress: invoiceConfig.senderAddress,
        status: "sent",
        updatedAt: serverTimestamp(),
      };

      let savedId = invoiceId;
      if (invoiceId) {
        await updateDoc(doc(db, "invoices", invoiceId), docData);
      } else {
        const docRef = await addDoc(collection(db, "invoices"), {
          ...docData,
          createdAt: serverTimestamp(),
        });
        savedId = docRef.id;
        setInvoiceId(docRef.id);
        // Update the config with the generated invoice number
        setInvoiceConfig((prev) => ({ ...prev, invoiceNumber: autoInvoiceNumber }));
      }

      // Generate QR and show success modal
      const invoiceUrl = `${BASE_URL}/invoice/${savedId}`;
      let qrDataUrl: string | null = null;
      try {
        qrDataUrl = await QRCodeLib.toDataURL(invoiceUrl, {
          width: 300,
          margin: 2,
          color: { dark: "#61CE70", light: "#ffffff" },
        });
      } catch {
        // QR generation failed, modal will still show without it
      }
      setInvoiceSuccessModal({ url: invoiceUrl, qrDataUrl });
    } catch (err) {
      console.error("Invoice save error:", err);
      toast.error("Failed to save invoice");
    } finally {
      setInvoiceSaving(false);
    }
  };

  // Copy link
  const copyLink = (url: string, linkType: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(linkType);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopiedLink(null), 2000);
  };

  // Generate QR code. CashApp Pay links get the dotted, branded CashApp style;
  // all other links use the standard square QR.
  const showQRCode = async (url: string, key: string) => {
    try {
      const qrUrl = url.includes("/cashapppay/")
        ? await generateCashAppQR(url)
        : await generateDottedQR(url);
      setQrModals((prev) => ({ ...prev, [key]: qrUrl }));
      setQrLinks((prev) => ({ ...prev, [key]: url }));
    } catch {
      toast.error("Failed to generate QR code");
    }
  };

  // URL builders
  const getPaymentUrl = () =>
    `${BASE_URL}/paymentRequest/${walletAddress}`;
  const getCoffeeUrl = () =>
    `${BASE_URL}/paymentRequest/buymeCoffee/${walletAddress}`;
  const getDonationUrl = () =>
    `${BASE_URL}/paymentRequest/donation/${walletAddress}`;
  const getInvoiceUrl = () =>
    invoiceId ? `${BASE_URL}/invoice/${invoiceId}` : null;

  const getSlugPaymentUrl = () =>
    savedSlug ? `${BASE_URL}/pay/${savedSlug}/payment` : null;
  const getSlugCoffeeUrl = () =>
    savedSlug ? `${BASE_URL}/pay/${savedSlug}/coffee` : null;
  const getSlugDonationUrl = () =>
    savedSlug ? `${BASE_URL}/pay/${savedSlug}/donate` : null;
  const getCashappPayUrl = () =>
    savedSlug ? `${BASE_URL}/cashapppay/${savedSlug}` : null;

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#61CE70] animate-spin" />
      </div>
    );
  }

  const linkCards = [
    {
      key: "request",
      title: "Request Payment",
      description: "Share a simple payment link for anyone to send you funds",
      icon: <Wallet className="w-6 h-6" />,
      iconColor: "text-[#61CE70]",
      iconBg: "bg-[#61CE70]/10",
      enabled: collectSettings.requestPaymentEnabled,
      toggleKey: "requestPaymentEnabled" as const,
      url: getPaymentUrl(),
      slugUrl: getSlugPaymentUrl(),
      expandable: true,
      editorType: "request" as const,
      views: pageViewCounts.request || 0,
    },
    {
      key: "coffee",
      title: "Support Page (Tips Jar)",
      description:
        "Receive tips and support from your audience",
      icon: <Gift className="w-6 h-6" />,
      iconColor: "text-yellow-400",
      iconBg: "bg-yellow-400/10",
      enabled: collectSettings.buyMeCoffeeEnabled,
      toggleKey: "buyMeCoffeeEnabled" as const,
      url: getCoffeeUrl(),
      slugUrl: getSlugCoffeeUrl(),
      expandable: true,
      editorType: "coffee" as const,
      views: pageViewCounts.buymeCoffee || 0,
    },
    {
      key: "donation",
      title: "Donation Box",
      description:
        "A customizable donation page with images, colors, and more",
      icon: <Heart className="w-6 h-6" />,
      iconColor: "text-pink-400",
      iconBg: "bg-pink-400/10",
      enabled: collectSettings.donationBoxEnabled,
      toggleKey: "donationBoxEnabled" as const,
      url: getDonationUrl(),
      slugUrl: getSlugDonationUrl(),
      expandable: true,
      editorType: "donation" as const,
      views: pageViewCounts.donation || 0,
    },
    {
      key: "invoice",
      title: "Create Invoice",
      description:
        "Generate a professional invoice and collect payment via a shareable link",
      icon: <FileText className="w-6 h-6" />,
      iconColor: "text-indigo-400",
      iconBg: "bg-indigo-400/10",
      enabled: collectSettings.invoiceEnabled,
      toggleKey: "invoiceEnabled" as const,
      url: getInvoiceUrl(),
      slugUrl: null,
      expandable: true,
      editorType: "invoice" as const,
      views: pageViewCounts.invoice || 0,
    },
  ];

  const toggleExpand = (editorType: "request" | "donation" | "coffee" | "invoice") => {
    const closeAll = () => {
      setDonationExpanded(false);
      setInvoiceExpanded(false);
      setPaymentLinkExpanded(false);
      setCoffeeExpanded(false);
    };

    if (editorType === "donation") {
      const next = !donationExpanded;
      closeAll();
      setDonationExpanded(next);
    } else if (editorType === "invoice") {
      const next = !invoiceExpanded;
      closeAll();
      setInvoiceExpanded(next);
    } else if (editorType === "request") {
      const next = !paymentLinkExpanded;
      closeAll();
      setPaymentLinkExpanded(next);
    } else if (editorType === "coffee") {
      const next = !coffeeExpanded;
      closeAll();
      setCoffeeExpanded(next);
    }
    setMobileTab("editor");
  };

  // Inline component for payment method toggles per type
  // Note: "card-bank" (Stripe) is intentionally excluded from collect pages.
  // Stripe OnRamp is only available on the user's own Add Funds page because
  // a single wallet address cannot be registered with multiple Stripe/Link identities.
  const PaymentMethodToggles = ({ type }: { type: string }) => {
    const disabled = disabledMethodsByType[type] || [];
    // Exclude card-bank from the count since it's not toggleable on collect pages,
    // and any globally-hidden method (e.g. `cashapp` while the peer extension is down).
    const isHidden = (id: string) =>
      id === "card-bank" || HIDDEN_PAYMENT_METHOD_IDS.includes(id);
    const collectMethodIds = ALL_PAYMENT_METHOD_IDS.filter((id) => !isHidden(id));
    const enabledCount = collectMethodIds.length - disabled.filter((id: string) => !isHidden(id)).length;

    const methodCards: { id: string; content: React.ReactNode }[] = [
      {
        id: "coinbase-onramp",
        content: (
          <div className="flex flex-col gap-3">
            {/* Card-network + wallet logos, each in its own white tile */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { src: "/Visa-logo.png", alt: "Visa" },
                { src: "/mastercard-logo.png", alt: "Mastercard" },
                { src: "/applepay-logo.png", alt: "Apple Pay" },
                { src: "/googlepay-logo.png", alt: "Google Pay" },
              ].map((logo) => (
                <div
                  key={logo.alt}
                  className="flex items-center justify-center rounded-lg bg-white px-1.5 py-1 shadow-sm"
                >
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="h-4 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-white leading-snug">
                <span className="text-[#61CE70]">Pay</span> with Visa /
                MasterCard, Apple Pay or Google Pay
              </h4>
              <p className="text-xs text-gray-400 mt-0.5">
                Instant and secure payment
              </p>
            </div>
            <div className="flex items-center gap-3 pt-3 border-t border-gray-800/60">
              <div className="flex items-center gap-1.5 bg-gray-900/40 text-white border border-gray-700/50 px-2.5 py-1 rounded-full font-semibold text-xs">
                <Zap
                  size={13}
                  className="text-[#61CE70]"
                  fill="currentColor"
                  strokeWidth={0}
                />
                <span>Instant</span>
              </div>
              <div className="flex items-center gap-1.5 text-indigo-300 font-semibold text-xs">
                <ShieldCheck size={13} strokeWidth={2.5} />
                <span>Secure Checkout</span>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "lightning-btc",
        content: (
          <div className="flex items-center gap-3 w-full">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#00D632]/12 border border-[#00D632]/20 flex items-center justify-center shrink-0">
                       <SiCashapp className="w-6 h-6 sm:w-9 sm:h-9 rounded-lg bg-white text-[#00D632]" />
                     </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-green-400">
                CashApp Pay
              </div>
              <span className="text-xs text-gray-400">
                Instant payment via Lightning
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "cashapp",
        content: (
          <div>
            <h3 className="font-semibold text-green-400 text-sm mb-1">
              Add Money via Wallet Apps
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              Fast & secure deposits with popular apps
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { src: "", isCashApp: true },
                { src: "/chime-logo.svg", alt: "Chime", border: true },
                { src: "/venmo.png", alt: "Venmo" },
                { src: "/zelle.png", alt: "Zelle" },
                { src: "/revoult.png", alt: "Revolut" },
                { src: "/wise.png", alt: "Wise" },
              ].map((app, i) => (
                <div
                  key={i}
                  className={`w-10 h-10 rounded-full overflow-hidden shadow-md bg-white ${app.border ? "border-[3px] border-green-400 p-0.5" : "p-0.5"}`}
                >
                  {app.isCashApp ? (
                    <SiCashapp className="w-full h-full bg-white text-green-500 rounded-full" />
                  ) : (
                    <img
                      src={app.src}
                      alt={app.alt}
                      className="w-full h-full object-contain"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: "wallet",
        content: (
          <div className="flex items-center gap-3 w-full">
            <div className="p-2 bg-[#61CE70]/10 rounded-xl shrink-0">
              <Wallet className="w-5 h-5 text-[#61CE70]" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#81E68A]">
                Wallet Transfer
              </div>
              <span className="text-xs text-gray-400">
                Transfer from your wallet
              </span>
            </div>
          </div>
        ),
      },
    ];

    return (
      <div className="border-t border-gray-700/50 pt-5">
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-medium text-gray-300">
            Accepted Payment Methods
          </label>
          <span className="text-xs text-gray-500">
            {enabledCount}/{collectMethodIds.length} enabled
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Toggle which payment methods are available on this page
        </p>
        <div className="space-y-3">
          {methodCards
            .filter((method) => !HIDDEN_PAYMENT_METHOD_IDS.includes(method.id))
            .map((method) => {
            const isEnabled = !disabled.includes(method.id);
            const isLastEnabled = isEnabled && enabledCount <= 1;
            return (
              <button
                key={method.id}
                type="button"
                onClick={() => {
                  if (isLastEnabled) {
                    toast.error("At least one payment method must be enabled");
                    return;
                  }
                  togglePaymentMethod(type, method.id);
                }}
                className={`relative w-full rounded-2xl border transition-all text-left ${
                  isEnabled
                    ? "border-gray-700/50 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]"
                    : "border-gray-800/30 bg-gray-900/30 opacity-35 hover:opacity-50"
                }`}
              >
                {/* Toggle top-right */}
                <div className={`absolute top-3 right-3 w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all z-10 ${
                  isEnabled ? "bg-[#61CE70]" : "border-2 border-gray-600"
                }`}>
                  {isEnabled && <Check size={12} className="text-white" strokeWidth={3} />}
                </div>

                <div className="p-4 pr-10">
                  {method.content}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl scrollbar-hide mx-auto text-white">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: { iconTheme: { primary: "#61CE70", secondary: "#fff" } },
          error: { iconTheme: { primary: "#EF4444", secondary: "#fff" } },
        }}
      />

      {/* QR Code Modals */}
      {Object.entries(qrModals).map(
        ([key, qrUrl]) =>
          qrUrl && (
            <div
              key={key}
              className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-[1000] p-4"
              onClick={() => setQrModals((prev) => ({ ...prev, [key]: null }))}
            >
              <div
                className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl w-full max-w-sm border border-gray-700/50 p-6 text-center"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-white">QR Code</h3>
                  <button
                    onClick={() =>
                      setQrModals((prev) => ({ ...prev, [key]: null }))
                    }
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
                {/* Seller identity — moved to the top of the modal, out of the QR */}
                {user?.username && (
                  <div className="flex items-center justify-center gap-2.5 mb-4">
                    {ownerPhotoURL ? (
                      <img
                        src={ownerPhotoURL}
                        alt={user.username}
                        className="w-9 h-9 rounded-full object-cover border border-gray-700/50"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#61CE70] flex items-center justify-center text-white text-base font-bold">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-white text-base font-semibold">
                      @{user.username}
                    </span>
                  </div>
                )}
                {/* Dotted QR with the CashTap logo embedded in the centre. */}
                <div className="bg-white p-4 rounded-2xl inline-block mb-4">
                  <img src={qrUrl} alt="QR Code" className="w-56 h-56" />
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Scan this QR code to open the payment page
                </p>
                {qrLinks[key] && (
                  <div className="flex items-center gap-2 bg-gray-800/40 border border-gray-700/50 rounded-xl p-2.5">
                    <span className="flex-1 min-w-0 truncate text-left text-sm text-gray-300">
                      {qrLinks[key]}
                    </span>
                    <button
                      onClick={() => copyLink(qrLinks[key], `qr-${key}`)}
                      className="shrink-0 p-2 rounded-lg hover:bg-white/10 transition-colors"
                      title="Copy link"
                    >
                      {copiedLink === `qr-${key}` ? (
                        <Check size={16} className="text-[#61CE70]" />
                      ) : (
                        <Copy size={16} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ),
      )}

      {/* Invoice Success Modal */}
      {invoiceSuccessModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-[1000] p-4"
          onClick={() => setInvoiceSuccessModal(null)}
        >
          <div
            className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl w-full max-w-md border border-gray-700/50 p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Invoice Created</h3>
              <button
                onClick={() => setInvoiceSuccessModal(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Success icon */}
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-[#61CE70]/15 flex items-center justify-center">
                <Check size={32} className="text-[#61CE70]" />
              </div>
            </div>

            <p className="text-gray-400 text-sm text-center mb-6">
              Your invoice is ready! Share this link with your client to collect
              payment.
            </p>

            {/* QR Code */}
            {invoiceSuccessModal.qrDataUrl && (
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 rounded-2xl">
                  <img
                    src={invoiceSuccessModal.qrDataUrl}
                    alt="Invoice QR Code"
                    className="w-52 h-52"
                  />
                </div>
              </div>
            )}

            {/* URL display */}
            <div className="p-3 bg-gray-800/30 border border-gray-700/30 rounded-xl mb-4">
              <p className="text-gray-500 text-xs mb-1">Invoice Link</p>
              <code className="text-[#61CE70] text-sm font-mono break-all">
                {invoiceSuccessModal.url}
              </code>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(invoiceSuccessModal.url);
                  toast.success("Link copied to clipboard!");
                }}
                className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-3 rounded-xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all"
              >
                <Copy size={18} />
                Copy
              </button>
              <button
                onClick={() => {
                  setInvoiceSuccessModal(null);
                  setShareModal({
                    url: invoiceSuccessModal.url,
                    title: "Invoice Payment",
                  });
                }}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-all"
              >
                <Share2 size={18} />
                Share
              </button>
              <a
                href={invoiceSuccessModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-5 py-3 border border-gray-700/50 text-gray-300 rounded-xl font-medium hover:bg-gray-800/30 transition-all"
              >
                <ExternalLink size={18} />
                Open
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModal && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-md z-[1000] p-4"
          onClick={() => setShareModal(null)}
        >
          <div
            className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] rounded-3xl w-full max-w-sm border border-gray-700/50 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-white">Share Link</h3>
              <button
                onClick={() => setShareModal(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                {
                  name: "WhatsApp",
                  color: "bg-green-600",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  ),
                  getUrl: (url: string, title: string) =>
                    `https://wa.me/?text=${encodeURIComponent(`${title}: ${url}`)}`,
                },
                {
                  name: "Telegram",
                  color: "bg-blue-500",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                  ),
                  getUrl: (url: string, title: string) =>
                    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
                },
                {
                  name: "Messenger",
                  color: "bg-gradient-to-br from-blue-500 to-purple-600",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                      <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8.2l3.131 3.259L19.752 8.2l-6.561 6.763z" />
                    </svg>
                  ),
                  getUrl: (url: string) =>
                    `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&app_id=291494419107518&redirect_uri=${encodeURIComponent(url)}`,
                },
                {
                  name: "X",
                  color: "bg-black",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  ),
                  getUrl: (url: string, title: string) =>
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
                },
                {
                  name: "Email",
                  color: "bg-gray-600",
                  icon: <Mail className="w-6 h-6 text-white" />,
                  getUrl: (url: string, title: string) =>
                    `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Here's my payment link: ${url}`)}`,
                },
                {
                  name: "Instagram",
                  color:
                    "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400",
                  icon: (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  ),
                  getUrl: () => null, // Instagram doesn't support direct URL sharing
                },
              ].map((platform) => (
                <button
                  key={platform.name}
                  onClick={() => {
                    const link = platform.getUrl(
                      shareModal.url,
                      shareModal.title,
                    );
                    if (link) {
                      window.open(
                        link,
                        "_blank",
                        "noopener,noreferrer,width=600,height=500",
                      );
                    } else {
                      // For Instagram, copy to clipboard instead
                      navigator.clipboard.writeText(shareModal.url);
                      toast.success(
                        "Link copied! Paste it in your Instagram story or bio.",
                      );
                    }
                  }}
                  className={`${platform.color} flex flex-col items-center gap-2 p-3 rounded-2xl hover:opacity-90 transition-all hover:scale-105`}
                >
                  {platform.icon}
                  <span className="text-white text-xs font-medium">
                    {platform.name}
                  </span>
                </button>
              ))}
            </div>

            {/* Copy link */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(shareModal.url);
                toast.success("Link copied to clipboard!");
              }}
              className="w-full flex items-center justify-center gap-2 py-3 bg-gray-800/50 border border-gray-700/50 rounded-xl text-gray-300 hover:bg-gray-700/50 transition-all"
            >
              <Copy size={16} />
              <span className="text-sm font-medium">Copy Link</span>
            </button>
          </div>
        </div>
      )}

      {/* Page Header + Tab Selector */}
      <div className="bg-gradient-to-br from-[#0a1210] via-[#0F1419] to-[#111a15] border border-[#61CE70]/20 rounded-3xl p-5 sm:p-7 mb-6 relative overflow-hidden">
        {/* Corner glow */}
        <div className="absolute top-0 left-0 w-2.5 h-2.5 rounded-full bg-[#61CE70] shadow-lg shadow-[#61CE70]/50 m-4" />
        <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#61CE70]/5 rounded-full blur-3xl pointer-events-none" />

        <h1 className="text-2xl sm:text-3xl font-bold text-white text-center mb-1">
          Collect
        </h1>
        <p className="text-gray-400 text-sm text-center mb-2">
          Create Payment Links, Invoices, Donations & Tip Jars
        </p>

        {/* Platform fee disclosure. Now shown to ALL Collect users
            (individual + business) since the fee was opened up beyond
            the business-only segment. Both percentage and flat are
            resolved from per-user override → global default → fallback,
            so users on a custom rate see their actual fee here, not the
            platform default. */}
        <p className="text-[11px] text-gray-500/90 text-center mb-6">
          A{" "}
          <span className="text-[#61CE70] font-semibold tabular-nums">
            {(collectFeePct * 100).toFixed(2)}% + ${collectFeeFlat.toFixed(2)}
          </span>{" "}
          platform fee applies to each Collect payment.
        </p>

        {/* CashApp Pay direct link — featured above the tab list so users
            find their share-ready one-tap URL immediately. Requires a saved
            custom slug (the route would 404 otherwise); if absent, we show a
            short hint pointing to the Universal Link tab. */}
        <div ref={cashappHeroRef} className="relative mb-5 sm:mb-6 z-10 scroll-mt-24">
          {new Date() < CASHAPP_NEW_UNTIL && (
            <span className="absolute -top-2 left-4 z-10 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-bold tracking-wider uppercase shadow-sm shadow-blue-500/40">
              New
            </span>
          )}
          <div className="rounded-2xl border border-[#00D632]/30 bg-gradient-to-br from-[#00D632]/10 via-[#0F1419]/40 to-[#00D632]/5 p-4 sm:p-5 shadow-[0_8px_24px_-12px_rgba(0,214,50,0.25)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#00D632]/20 border border-[#00D632]/30 flex items-center justify-center shrink-0">
                <SiCashapp className="w-5 h-5 sm:w-6 sm:h-6 text-[#00D632]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm sm:text-base font-bold text-white leading-tight">
                  CashApp Pay Link
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5">
                  Direct one-tap CashApp checkout — share with anyone to receive payments instantly.
                </p>
              </div>
            </div>

            {getCashappPayUrl() ? (
              <div className="flex items-center justify-between gap-2 p-2.5 sm:p-3 bg-[#0A0D12]/70 border border-gray-800/60 rounded-xl">
                <code className="text-[#00D632] text-xs sm:text-sm font-mono break-all flex-1 min-w-0">
                  {getCashappPayUrl()}
                </code>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => copyLink(getCashappPayUrl()!, "cashapppay-hero")}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    {copiedLink === "cashapppay-hero" ? (
                      <Check size={16} className="text-[#00D632]" />
                    ) : (
                      <Copy size={16} className="text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => showQRCode(getCashappPayUrl()!, "cashapppay-hero")}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Show QR code"
                  >
                    <QrCode size={16} className="text-gray-400" />
                  </button>
                  <button
                    onClick={() =>
                      setShareModal({
                        url: getCashappPayUrl()!,
                        title: "CashApp Pay Link",
                      })
                    }
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Share"
                  >
                    <Share2 size={16} className="text-gray-400" />
                  </button>
                  <a
                    href={getCashappPayUrl()!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink size={16} className="text-gray-400" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 p-2.5 sm:p-3 bg-[#0A0D12]/70 border border-gray-800/60 rounded-xl">
                <AlertCircle size={14} className="text-yellow-400 shrink-0 mt-0.5" />
                <p className="text-xs text-gray-400 leading-relaxed">
                  Set a custom slug in{" "}
                  <span className="text-[#61CE70] font-medium">Universal Link</span>{" "}
                  below to unlock your CashApp Pay link.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tab buttons */}
        <div className="space-y-2.5 relative z-10">
          {[
            {
              key: "universal" as const,
              label: "Universal Link",
              desc: "One shareable link that opens a minimal, secure payment page.",
              icon: <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-[#61CE70]" />,
              isNew: false,
            },
            {
              key: "donation" as const,
              label: "Create Donation Box",
              desc: "Start a fundraiser and accept donations globally.",
              icon: <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-[#61CE70]" />,
              isNew: false,
            },
            {
              key: "coffee" as const,
              label: "Create Support Page (Tips Jar)",
              desc: "Receive tips and support from your audience.",
              icon: <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-[#61CE70]" />,
              isNew: false,
            },
            {
              key: "invoice" as const,
              label: "Create Invoice",
              desc: "Send professional invoices and track payments.",
              icon: (
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-[#61CE70]" />
              ),
              isNew: false,
            },
          ].map((tab:any) => (
            <button
              key={tab.key}
              onClick={() => {
                setCollectTab(tab.key);
                // Auto-expand editor for the selected tab
                setDonationExpanded(tab.key === "donation");
                setInvoiceExpanded(tab.key === "invoice");
                setPaymentLinkExpanded(tab.key === "request");
                setCoffeeExpanded(tab.key === "coffee");
                setMobileTab("editor");
                setTimeout(() => {
                  // CashApp Pay Link has no editor below — scroll to its hero
                  // card instead so the share-ready link is what comes into view.
                  const target = contentSectionRef.current;
                  target?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }, 100);
              }}
              className={`w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 sm:py-4 rounded-2xl border transition-all group ${
                collectTab === tab.key
                  ? "bg-[#61CE70]/10 border-[#61CE70]/30"
                  : "bg-gray-800/40 border-gray-700/30 hover:bg-gray-800/60 hover:border-gray-600/40"
              }`}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className={`p-2 rounded-xl shrink-0 ${
                    collectTab === tab.key
                      ? "bg-[#61CE70]/15"
                      : "bg-gray-700/30 group-hover:bg-gray-700/50"
                  }`}
                >
                  {tab.icon}
                </div>
                <div className="text-left">
                  <p
                    className={`font-semibold text-sm sm:text-base flex items-center gap-2 ${
                      collectTab === tab.key ? "text-white" : "text-gray-200"
                    }`}
                  >
                    {tab.label}
                    {tab.isNew && new Date() < CASHAPP_NEW_UNTIL && (
                      <span className="px-1.5 py-0.5 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[9px] font-bold tracking-wider uppercase shadow-sm shadow-blue-500/40">
                        New
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 hidden sm:block">
                    {tab.desc}
                  </p>
                </div>
              </div>
              <ChevronRight
                className={`w-5 h-5 shrink-0 transition-colors ${
                  collectTab === tab.key ? "text-[#61CE70]" : "text-gray-600"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Content Section — scroll target */}
      <div ref={contentSectionRef} />

      {/* Universal Link Section */}
      {collectTab === "universal" && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] xl:grid-cols-[1fr_500px] gap-6 items-start">
          {/* LEFT: Editor */}
          <div className="space-y-6">
            {/* Mobile tab toggle */}
            <div className="lg:hidden flex gap-2">
              <button
                onClick={() => setMobileTab("editor")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  mobileTab === "editor"
                    ? "bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/30"
                    : "bg-gray-800/30 text-gray-400 border border-gray-700/50"
                }`}
              >
                <PenLine size={16} />
                Editor
              </button>
              <button
                onClick={() => setMobileTab("preview")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  mobileTab === "preview"
                    ? "bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/30"
                    : "bg-gray-800/30 text-gray-400 border border-gray-700/50"
                }`}
              >
                <Eye size={16} />
                Preview
              </button>
            </div>

            <div className={`space-y-6 ${mobileTab === "preview" ? "hidden lg:block" : ""}`}>
              {/* Slug Setup */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 md:p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#61CE70]/10 rounded-xl">
                    <Globe className="w-5 h-5 text-[#61CE70]" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-white">
                      Custom URL
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Create a short and memorable link for your page.
                    </p>
                  </div>
                </div>

                {savedSlug && !editingSlug ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-[#61CE70]/10 border border-[#61CE70]/30 rounded-2xl">
                      <p className="text-gray-400 text-xs mb-1">Your custom URL</p>
                      <div className="flex items-center justify-between gap-3">
                        <code className="text-[#61CE70] font-mono text-sm sm:text-base break-all">
                          {BASE_URL}/pay/{savedSlug}
                        </code>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => copyLink(`${BASE_URL}/pay/${savedSlug}`, "slug")} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Copy link">
                            {copiedLink === "slug" ? <Check size={18} className="text-[#61CE70]" /> : <Copy size={18} className="text-gray-400" />}
                          </button>
                          <button onClick={() => showQRCode(`${BASE_URL}/pay/${savedSlug}`, "universal-slug")} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Show QR code">
                            <QrCode size={18} className="text-gray-400" />
                          </button>
                          <button onClick={() => setShareModal({ url: `${BASE_URL}/pay/${savedSlug}`, title: "Universal Link" })} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Share link">
                            <Share2 size={18} className="text-gray-400" />
                          </button>
                          <a href={`${BASE_URL}/pay/${savedSlug}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="Open in new tab">
                            <ExternalLink size={18} className="text-gray-400" />
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setEditingSlug(true)} className="px-4 py-2 text-sm font-medium text-gray-300 border border-gray-700/50 rounded-xl hover:bg-gray-800/30 transition-all">
                        Edit
                      </button>
                      <button onClick={handleRemoveSlug} disabled={slugLoading} className="px-4 py-2 text-sm font-medium text-red-400 border border-red-900/50 rounded-xl hover:bg-red-900/20 transition-all disabled:opacity-50">
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm whitespace-nowrap hidden sm:block">
                        {BASE_URL}/pay/
                      </span>
                      <input
                        type="text"
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
                          setSlugError(null);
                        }}
                        className="flex-1 px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                        placeholder="your-custom-slug"
                        maxLength={30}
                      />
                    </div>
                    {slug && <p className="text-gray-500 text-xs sm:hidden">Preview: {BASE_URL}/pay/{slug}</p>}
                    {slugError && (
                      <div className="flex items-center gap-2 text-red-400 text-sm">
                        <AlertCircle size={16} />
                        {slugError}
                      </div>
                    )}
                    <div className="flex gap-3">
                      <button onClick={handleSlugSave} disabled={slugLoading || !slug} className="px-6 py-2.5 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                        {slugLoading ? (<><Loader2 size={16} className="animate-spin" />Saving...</>) : "Save Link"}
                      </button>
                      {editingSlug && (
                        <button onClick={() => { setEditingSlug(false); setSlug(savedSlug || ""); setSlugError(null); }} className="px-4 py-2 text-sm font-medium text-gray-400 border border-gray-700/50 rounded-xl hover:bg-gray-800/30 transition-all">
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Page — minimal, image-free customization */}
              <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 backdrop-blur-sm space-y-5">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-white">Payment Page</h3>
                  <p className="text-gray-400 text-xs sm:text-sm mt-1">A clean, secure payment card — no images or banners, so buyers pay without scrolling.</p>
                </div>

                {/* Heading */}
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Heading</label>
                  <input
                    type="text"
                    value={universalConfig.title}
                    onChange={(e) => setUniversalConfig((c) => ({ ...c, title: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                    placeholder="Secure Payment"
                    maxLength={60}
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Subtitle</label>
                  <input
                    type="text"
                    value={universalConfig.bio}
                    onChange={(e) => setUniversalConfig((c) => ({ ...c, bio: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                    placeholder="Complete your transaction safely"
                    maxLength={80}
                  />
                </div>

                {/* Button text */}
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Button Text</label>
                  <input
                    type="text"
                    value={universalConfig.buttonText}
                    onChange={(e) => setUniversalConfig((c) => ({ ...c, buttonText: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                    placeholder="Top Up Now"
                    maxLength={30}
                  />
                </div>

                {/* Min / Max amount */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">Min Amount ($)</label>
                    <input
                      type="number"
                      min={0.5}
                      step="0.5"
                      value={universalConfig.minAmount}
                      onChange={(e) => setUniversalConfig((c) => ({ ...c, minAmount: Math.max(0.5, parseFloat(e.target.value) || 0) }))}
                      className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">Max Amount ($)</label>
                    <input
                      type="number"
                      min={1}
                      step="1"
                      value={universalConfig.maxAmount}
                      onChange={(e) => setUniversalConfig((c) => ({ ...c, maxAmount: Math.max(1, parseFloat(e.target.value) || 0) }))}
                      className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
                    />
                  </div>
                </div>

                {/* Accent Color */}
                <div>
                  <label className="text-sm font-medium text-gray-300 mb-2 block">Accent Color</label>
                  <div className="flex flex-wrap gap-2 items-center">
                    {["#61CE70", "#EC4899", "#8B5CF6", "#3B82F6", "#06B6D4", "#10B981", "#F59E0B", "#EF4444"].map((c) => (
                      <button
                        key={c}
                        onClick={() => setUniversalConfig((prev) => ({ ...prev, primaryColor: c }))}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          universalConfig.primaryColor === c ? "border-white scale-110" : "border-transparent hover:scale-105"
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                    {/* Custom color picker */}
                    <input
                      type="color"
                      value={universalConfig.primaryColor}
                      onChange={(e) => setUniversalConfig((prev) => ({ ...prev, primaryColor: e.target.value }))}
                      className="w-8 h-8 rounded-full cursor-pointer border-0 bg-transparent shrink-0"
                      title="Custom color"
                    />
                    {/* Hex value input */}
                    <input
                      type="text"
                      value={universalConfig.primaryColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setUniversalConfig((prev) => ({ ...prev, primaryColor: v.startsWith("#") ? v : `#${v}` }));
                      }}
                      maxLength={7}
                      placeholder="#61CE70"
                      className="w-24 px-2.5 py-1.5 text-sm font-mono uppercase rounded-lg bg-gray-800/50 border border-gray-700/50 text-white focus:outline-none focus:border-[#61CE70] transition-colors"
                    />
                  </div>
                </div>

                {/* Accepted payment methods — controls the same `request`
                    disabled-set the public /pay/:slug/payment page reads. */}
                <PaymentMethodToggles type="request" />
              </div>

              {/* Save Button */}
              <button
                onClick={handleUniversalSave}
                disabled={universalSaving}
                className="w-full py-3 bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white rounded-2xl font-semibold hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {universalSaving ? (
                  <><Loader2 size={18} className="animate-spin" />Saving...</>
                ) : (
                  "Save Universal Link"
                )}
              </button>
            </div>
          </div>

          {/* RIGHT: Live Preview */}
          <div className={`${mobileTab === "preview" ? "block" : "hidden lg:block"} lg:sticky lg:top-0 lg:self-start`} style={{ maxHeight: "calc(100vh - 7rem)" }}>
            <div className="bg-gradient-to-br mt-12 from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl overflow-hidden flex flex-col" style={{ maxHeight: "calc(100vh - 7rem)" }}>
              <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-gray-700/30 shrink-0">
                <Eye size={16} className="text-gray-400" />
                <p className="text-sm font-medium text-gray-400">Live Preview</p>
              </div>
              <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent p-5">
                <UniversalLinkPreview
                  config={universalConfig}
                  ownerUsername={user?.username || ""}
                  ownerPhotoURL={user?.photoURL || null}
                  collectSettings={{
                    donationBoxEnabled: collectSettings.donationBoxEnabled,
                    requestPaymentEnabled: collectSettings.requestPaymentEnabled,
                    buyMeCoffeeEnabled: collectSettings.buyMeCoffeeEnabled,
                  }}
                  disabledPaymentMethods={disabledMethodsByType}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sections below show for non-universal tabs */}
      {collectTab !== "universal" && collectTab !== "cashapppay" && (
        <>
          {/* Payment Page Theme */}
          <div
            className="
  bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]
  border border-gray-700/50
  rounded-2xl sm:rounded-3xl
  p-4 sm:p-5 lg:p-6
  backdrop-blur-sm
  mb-5 sm:mb-6
"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
              {/* Left side - icon + text */}
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                <div
                  className="
        p-2.5 sm:p-3
        bg-purple-500/10
        rounded-xl sm:rounded-2xl
        shrink-0
      "
                >
                  {collectSettings.paymentTheme === "dark" ? (
                    <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                  ) : (
                    <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
                  )}
                </div>

                <div>
                  <h3
                    className="
          text-base sm:text-lg md:text-xl
          font-bold text-white
          leading-tight
        "
                  >
                    Payment Page Theme
                  </h3>
                  <p
                    className="
          text-gray-400
          text-xs sm:text-sm
          mt-0.5 sm:mt-1
          max-w-md
        "
                  >
                    Choose dark or light mode for all your public payment pages
                  </p>
                </div>
              </div>

              {/* Toggle button */}
              <button
                onClick={async () => {
                  const newTheme =
                    collectSettings.paymentTheme === "dark" ? "light" : "dark";
                  const newSettings = {
                    ...collectSettings,
                    paymentTheme: newTheme as "dark" | "light",
                  };
                  setCollectSettings(newSettings);
                  try {
                    await updateDoc(doc(db, "users", walletAddress), {
                      collectSettings: newSettings,
                    });
                    toast.success(`Switched to ${newTheme} mode`);
                  } catch {
                    setCollectSettings(collectSettings);
                    toast.error("Failed to update theme");
                  }
                }}
                type="button"
                className="
        relative
        w-14 h-7 sm:w-16 sm:h-8
        rounded-full
        transition-colors duration-300
        touch-manipulation
        shrink-0
        active:scale-95
      "
                style={{
                  backgroundColor:
                    collectSettings.paymentTheme === "light"
                      ? "#61CE70"
                      : "#374151",
                }}
              >
                <div
                  className={`
          absolute top-0.5 sm:top-1
          w-6 h-6 sm:w-6 sm:h-6
          rounded-full
          bg-white shadow-md
          flex items-center justify-center
          transition-all duration-300 ease-out
          ${collectSettings.paymentTheme === "light" ? "left-7 sm:left-9" : "left-0.5"}
        `}
                >
                  {collectSettings.paymentTheme === "dark" ? (
                    <Moon size={14} className="text-gray-700" />
                  ) : (
                    <Sun size={14} className="text-amber-500" />
                  )}
                </div>
              </button>
            </div>

            {/* Bottom status row */}
            <div
              className="
    mt-4 sm:mt-5
    flex flex-wrap items-center gap-2 sm:gap-3
    text-xs sm:text-sm
  "
            >
              <span
                className={`
      font-medium
      px-2.5 py-1 sm:px-3 sm:py-1.5
      rounded-full
      whitespace-nowrap
      ${
        collectSettings.paymentTheme === "dark"
          ? "bg-gray-700/50 text-gray-300"
          : "bg-amber-500/10 text-amber-400"
      }
    `}
              >
                {collectSettings.paymentTheme === "dark"
                  ? "Dark Mode"
                  : "Light Mode"}
              </span>

              <span className="text-gray-500">
                Applies to payment links, donations, invoices & support pages
              </span>
            </div>
          </div>

          {/* Page Impressions Analytics — Collapsible */}
          {
            <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 backdrop-blur-sm mb-6">
              <button
                onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#61CE70]/10 rounded-xl">
                    <BarChart3 className="w-5 h-5 text-[#61CE70]" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base sm:text-lg font-bold text-white">
                      Page Impressions
                    </h3>
                    <p className="text-gray-400 text-xs sm:text-sm">
                      {pageViewCounts.request +
                        pageViewCounts.buymeCoffee +
                        pageViewCounts.donation +
                        pageViewCounts.invoice}{" "}
                      total visits
                    </p>
                  </div>
                </div>
                {analyticsExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {analyticsExpanded && (
                <div className="mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-gray-800/30 rounded-xl p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Wallet size={14} className="text-[#61CE70]" />
                        <span className="text-gray-400 text-xs">Payment</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {pageViewCounts.request}
                      </p>
                    </div>
                    <div className="bg-gray-800/30 rounded-xl p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Gift size={14} className="text-yellow-400" />
                        <span className="text-gray-400 text-xs">Tips Jar</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {pageViewCounts.buymeCoffee}
                      </p>
                    </div>
                    <div className="bg-gray-800/30 rounded-xl p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <Heart size={14} className="text-pink-400" />
                        <span className="text-gray-400 text-xs">Donation</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {pageViewCounts.donation}
                      </p>
                    </div>
                    <div className="bg-gray-800/30 rounded-xl p-3 text-center">
                      <div className="flex items-center justify-center gap-1.5 mb-1">
                        <FileText size={14} className="text-indigo-400" />
                        <span className="text-gray-400 text-xs">Invoice</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {pageViewCounts.invoice}
                      </p>
                    </div>
                  </div>

                  {/* Recent Activity — last 3 + View All */}
                  {recentViews.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-700/50">
                      <p className="text-gray-400 text-xs font-medium mb-2">
                        Recent Visits
                      </p>
                      <div className="space-y-1.5">
                        {recentViews.slice(0, 3).map((view, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  view.type === "request"
                                    ? "bg-[#61CE70]"
                                    : view.type === "buymeCoffee"
                                      ? "bg-yellow-400"
                                      : view.type === "donation"
                                        ? "bg-pink-400"
                                        : "bg-indigo-400"
                                }`}
                              />
                              <span className="text-gray-300">
                                {view.type === "buymeCoffee"
                                  ? "Support Page"
                                  : view.type === "request"
                                    ? "Payment"
                                    : view.type === "donation"
                                      ? "Donation"
                                      : "Invoice"}
                              </span>
                            </div>
                            <span className="text-gray-500">
                              {moment(view.timestamp).fromNow()}
                            </span>
                          </div>
                        ))}
                      </div>
                      {recentViews.length > 3 && (
                        <button
                          onClick={() => {
                            setViewAllModalOpen(true);
                            setViewAllFilter("all");
                          }}
                          className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#61CE70] hover:text-[#61CE70]/80 transition-colors"
                        >
                          View All ({recentViews.length})
                          <ChevronRight size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          }

          {/* View All Visits Modal */}
          {viewAllModalOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={() => setViewAllModalOpen(false)}
              />
              <div className="relative bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                {/* Modal header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-700/50">
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      All Page Visits
                    </h3>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {recentViews.length} total visits
                    </p>
                  </div>
                  <button
                    onClick={() => setViewAllModalOpen(false)}
                    className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <X size={18} className="text-gray-400" />
                  </button>
                </div>

                {/* Category filter */}
                <div className="flex gap-2 px-5 py-3 border-b border-gray-700/50 overflow-x-auto scrollbar-hide">
                  {[
                    { key: "all", label: "All" },
                    { key: "request", label: "Payment" },
                    { key: "buymeCoffee", label: "Tips Jar" },
                    { key: "donation", label: "Donation" },
                    { key: "invoice", label: "Invoice" },
                  ].map((cat) => (
                    <button
                      key={cat.key}
                      onClick={() => setViewAllFilter(cat.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                        viewAllFilter === cat.key
                          ? "bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/30"
                          : "bg-gray-800/50 text-gray-400 border border-gray-700/30 hover:text-gray-300"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Visits list */}
                <div className="flex-1 overflow-y-auto p-5 space-y-2">
                  {recentViews
                    .filter(
                      (v) =>
                        viewAllFilter === "all" || v.type === viewAllFilter,
                    )
                    .map((view, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 bg-gray-800/20 rounded-xl text-sm"
                      >
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              view.type === "request"
                                ? "bg-[#61CE70]"
                                : view.type === "buymeCoffee"
                                  ? "bg-yellow-400"
                                  : view.type === "donation"
                                    ? "bg-pink-400"
                                    : "bg-indigo-400"
                            }`}
                          />
                          <span className="text-gray-200">
                            {view.type === "buymeCoffee"
                              ? "Support Page"
                              : view.type === "request"
                                ? "Payment"
                                : view.type === "donation"
                                  ? "Donation"
                                  : "Invoice"}
                          </span>
                        </div>
                        <span className="text-gray-500 text-xs">
                          {moment(view.timestamp).format(
                            "MMM D, YYYY [at] h:mm A",
                          )}
                        </span>
                      </div>
                    ))}
                  {recentViews.filter(
                    (v) => viewAllFilter === "all" || v.type === viewAllFilter,
                  ).length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-8">
                      No visits in this category
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Link Cards — split layout when editor is open */}
          <div
            className={
              anyEditorOpen
                ? "grid grid-cols-1 lg:grid-cols-[1fr_440px] xl:grid-cols-[1fr_500px] gap-6 items-start"
                : "block"
            }
          >
            {/* LEFT: Cards list */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {collectTab === "request"
                    ? "Payment Link"
                    : collectTab === "coffee"
                      ? "Support Page (Tips Jar)"
                      : collectTab === "donation"
                        ? "Donation Box"
                        : collectTab === "invoice"
                          ? "Invoice"
                          : "Your Payment Links"}
                </h2>
              </div>

              {/* Mobile tab toggle — fixed at top while scrolling */}
              {anyEditorOpen && (
                <div className="lg:hidden sticky top-0 z-30 -mx-4 sm:-mx-6 md:-mx-8 lg:-mx-10 px-4 sm:px-6 md:px-8 lg:px-10 py-3 bg-[#090D14]/95 backdrop-blur-md">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setMobileTab("editor")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        mobileTab === "editor"
                          ? "bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/30"
                          : "bg-gray-800/30 text-gray-400 border border-gray-700/50"
                      }`}
                    >
                      <PenLine size={16} />
                      Editor
                    </button>
                    <button
                      onClick={() => setMobileTab("preview")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        mobileTab === "preview"
                          ? "bg-[#61CE70]/15 text-[#61CE70] border border-[#61CE70]/30"
                          : "bg-gray-800/30 text-gray-400 border border-gray-700/50"
                      }`}
                    >
                      <Eye size={16} />
                      Preview
                    </button>
                  </div>
                </div>
              )}

              {/* Cards */}
              <div
                className={`space-y-4 ${anyEditorOpen && mobileTab === "preview" ? "hidden lg:block" : ""}`}
              >
                {linkCards
                  .filter((card) => {
                    const tabToKey: Record<string, string> = {
                      request: "request",
                      coffee: "coffee",
                      donation: "donation",
                      invoice: "invoice",
                    };
                    return card.key === tabToKey[collectTab];
                  })
                  .map((card) => (
                    <div
                      key={card.key}
                      className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 backdrop-blur-sm"
                    >
                      {/* Card Header */}
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl ${card.iconBg}`}>
                            <div className={card.iconColor}>{card.icon}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white">
                                {card.title}
                              </h3>
                              {card.views > 0 && (
                                <span className="flex items-center gap-1 text-xs font-medium text-gray-400 bg-gray-800/50 px-2 py-0.5 rounded-full">
                                  <EyeIcon size={12} />
                                  {card.views}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm">
                              {card.description}
                            </p>
                          </div>
                        </div>

                        {/* Toggle */}
                        <button
                          onClick={() => toggleSetting(card.toggleKey)}
                          className={`relative w-12 h-6 rounded-full transition-colors shrink-0 mt-1 ${
                            card.enabled ? "bg-[#61CE70]" : "bg-gray-700"
                          }`}
                        >
                          <div
                            className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                              card.enabled ? "translate-x-6" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </div>

                      {card.enabled && (
                        <>
                          {/* Primary URL (wallet address) */}
                          {card.url && (
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-800/30 border border-gray-700/30 rounded-xl">
                                <p className="text-gray-500 text-xs mb-1">
                                  Payment Link
                                </p>
                                <div className="flex items-center justify-between gap-2">
                                  <code className="text-gray-300 text-xs sm:text-sm font-mono break-all flex-1">
                                    {card.url}
                                  </code>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      onClick={() =>
                                        copyLink(card.url!, `${card.key}-url`)
                                      }
                                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                      title="Copy link"
                                    >
                                      {copiedLink === `${card.key}-url` ? (
                                        <Check
                                          size={16}
                                          className="text-[#61CE70]"
                                        />
                                      ) : (
                                        <Copy
                                          size={16}
                                          className="text-gray-400"
                                        />
                                      )}
                                    </button>
                                    <button
                                      onClick={() =>
                                        showQRCode(card.url!, card.key)
                                      }
                                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                      title="Show QR code"
                                    >
                                      <QrCode
                                        size={16}
                                        className="text-gray-400"
                                      />
                                    </button>
                                    <button
                                      onClick={() =>
                                        setShareModal({
                                          url: card.url!,
                                          title: card.title,
                                        })
                                      }
                                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                      title="Share"
                                    >
                                      <Share2
                                        size={16}
                                        className="text-gray-400"
                                      />
                                    </button>
                                    <a
                                      href={card.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                      title="Open in new tab"
                                    >
                                      <ExternalLink
                                        size={16}
                                        className="text-gray-400"
                                      />
                                    </a>
                                  </div>
                                </div>
                              </div>

                              {/* Slug URL */}
                              {card.slugUrl && (
                                <div className="p-3 bg-[#61CE70]/5 border border-[#61CE70]/20 rounded-xl">
                                  <p className="text-[#61CE70]/60 text-xs mb-1">
                                    Short Link
                                  </p>
                                  <div className="flex items-center justify-between gap-2">
                                    <code className="text-[#61CE70] text-xs sm:text-sm font-mono break-all flex-1">
                                      {card.slugUrl}
                                    </code>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        onClick={() =>
                                          copyLink(
                                            card.slugUrl!,
                                            `${card.key}-slug`,
                                          )
                                        }
                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                        title="Copy short link"
                                      >
                                        {copiedLink === `${card.key}-slug` ? (
                                          <Check
                                            size={16}
                                            className="text-[#61CE70]"
                                          />
                                        ) : (
                                          <Copy
                                            size={16}
                                            className="text-gray-400"
                                          />
                                        )}
                                      </button>
                                      <button
                                        onClick={() =>
                                          showQRCode(
                                            card.slugUrl!,
                                            `${card.key}-slug`,
                                          )
                                        }
                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                        title="Show QR code"
                                      >
                                        <QrCode
                                          size={16}
                                          className="text-gray-400"
                                        />
                                      </button>
                                      <button
                                        onClick={() =>
                                          setShareModal({
                                            url: card.slugUrl!,
                                            title: card.title,
                                          })
                                        }
                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                        title="Share short link"
                                      >
                                        <Share2
                                          size={16}
                                          className="text-gray-400"
                                        />
                                      </button>
                                      <a
                                        href={card.slugUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                        title="Open in new tab"
                                      >
                                        <ExternalLink
                                          size={16}
                                          className="text-gray-400"
                                        />
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}

                            </div>
                          )}

                          {/* Invoice: show "save first" message when no URL yet */}
                          {card.key === "invoice" && !card.url && (
                            <div className="p-3 bg-gray-800/20 border border-gray-700/30 rounded-xl">
                              <p className="text-gray-500 text-sm text-center">
                                Fill in the invoice details and save to generate
                                a shareable link
                              </p>
                            </div>
                          )}

                          {/* Editor section — always visible for expandable cards */}
                          {card.expandable && card.editorType && (
                            <div className="mt-4">
                              <button
                                onClick={() => toggleExpand(card.editorType!)}
                                className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                              >
                                {(card.editorType === "donation" &&
                                  donationExpanded) ||
                                (card.editorType === "invoice" &&
                                  invoiceExpanded) ||
                                (card.editorType === "request" &&
                                  paymentLinkExpanded) ||
                                (card.editorType === "coffee" &&
                                  coffeeExpanded) ? (
                                  <ChevronUp size={18} />
                                ) : (
                                  <ChevronDown size={18} />
                                )}
                                {card.editorType === "donation"
                                  ? donationExpanded
                                    ? "Hide Editor"
                                    : "Customize Donation Page"
                                  : card.editorType === "invoice"
                                    ? invoiceExpanded
                                      ? "Hide Editor"
                                      : "Create / Edit Invoice"
                                    : card.editorType === "request"
                                      ? paymentLinkExpanded
                                        ? "Hide Editor"
                                        : "Customize Payment Page"
                                      : coffeeExpanded
                                        ? "Hide Editor"
                                        : "Customize Tips Jar Page"}
                              </button>

                              {card.editorType === "donation" &&
                                donationExpanded && (
                                  <div className="mt-4 pt-4 border-t border-gray-700/50 space-y-6">
                                    <DonationBoxEditor
                                      config={donationConfig}
                                      onChange={setDonationConfig}
                                      walletAddress={walletAddress}
                                      onSave={handleDonationSave}
                                      saving={donationSaving}
                                    />
                                    <PaymentMethodToggles type="donation" />
                                  </div>
                                )}

                              {card.editorType === "invoice" &&
                                invoiceExpanded && (
                                  <div className="mt-4 pt-4 border-t border-gray-700/50 space-y-6">
                                    <InvoiceEditor
                                      config={invoiceConfig}
                                      onChange={setInvoiceConfig}
                                      walletAddress={walletAddress}
                                      onSave={handleInvoiceSave}
                                      saving={invoiceSaving}
                                    />
                                    <PaymentMethodToggles type="invoice" />
                                  </div>
                                )}

                              {card.editorType === "request" &&
                                paymentLinkExpanded && (
                                  <div className="mt-4 pt-4 border-t border-gray-700/50 space-y-6">
                                    <DonationBoxEditor
                                      config={paymentLinkConfig}
                                      onChange={setPaymentLinkConfig}
                                      walletAddress={walletAddress}
                                      onSave={handlePaymentLinkSave}
                                      saving={paymentLinkSaving}
                                      saveLabel="Save Payment Page"
                                      storagePrefix="payment-link-images"
                                    />
                                    <PaymentMethodToggles type="request" />
                                  </div>
                                )}

                              {card.editorType === "coffee" &&
                                coffeeExpanded && (
                                  <div className="mt-4 pt-4 border-t border-gray-700/50 space-y-6">
                                    <DonationBoxEditor
                                      config={coffeeConfig}
                                      onChange={setCoffeeConfig}
                                      walletAddress={walletAddress}
                                      onSave={handleCoffeeSave}
                                      saving={coffeeSaving}
                                      saveLabel="Save Tips Jar Page"
                                      storagePrefix="coffee-images"
                                    />
                                    <PaymentMethodToggles type="coffee" />
                                  </div>
                                )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
              </div>
            </div>

            {/* RIGHT: Preview panel — sticky on desktop, scrollable internally */}
            {anyEditorOpen && (
              <div
                className={`
              ${mobileTab === "preview" ? "block" : "hidden lg:block"}
              lg:sticky lg:top-0 lg:self-start
            `}
                style={{ maxHeight: "calc(100vh - 7rem)" }}
              >
                <div
                  className="bg-gradient-to-br mt-12 from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl overflow-hidden flex flex-col"
                  style={{ maxHeight: "calc(100vh - 7rem)" }}
                >
                  {/* Fixed header */}
                  <div className="flex items-center gap-2 px-5 pt-5 pb-3 border-b border-gray-700/30 shrink-0">
                    <Eye size={16} className="text-gray-400" />
                    <p className="text-sm font-medium text-gray-400">
                      Live Preview
                    </p>
                  </div>

                  {/* Scrollable content */}
                  <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent p-5">
                    {donationExpanded && (
                      <DonationPreview
                        config={donationConfig}
                        ownerUsername={user?.username || ""}
                        ownerPhotoURL={user?.photoURL || null}
                        pageType="donation"
                        disabledPaymentMethods={disabledMethodsByType.donation}
                      />
                    )}

                    {invoiceExpanded && (
                      <InvoicePreview
                        config={invoiceConfig}
                        ownerUsername={user?.username || ""}
                        ownerPhotoURL={user?.photoURL || null}
                      />
                    )}

                    {paymentLinkExpanded && (
                      <DonationPreview
                        config={paymentLinkConfig}
                        ownerUsername={user?.username || ""}
                        ownerPhotoURL={user?.photoURL || null}
                        pageType="request"
                        disabledPaymentMethods={disabledMethodsByType.request}
                      />
                    )}

                    {coffeeExpanded && (
                      <DonationPreview
                        config={coffeeConfig}
                        ownerUsername={user?.username || ""}
                        ownerPhotoURL={user?.photoURL || null}
                        pageType="coffee"
                        disabledPaymentMethods={disabledMethodsByType.coffee}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Info Footer */}
      <div className="mt-8 bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-5 sm:p-6 backdrop-blur-sm">
        <h4 className="text-white font-semibold mb-3">How it works</h4>
        <ul className="space-y-2 text-gray-400 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-[#61CE70] font-bold mt-0.5">1.</span>
            Enable the payment links you want to use
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#61CE70] font-bold mt-0.5">2.</span>
            Optionally create a custom slug for shorter, memorable URLs
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#61CE70] font-bold mt-0.5">3.</span>
            Customize your donation page or create invoices with the built-in
            editors
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#61CE70] font-bold mt-0.5">4.</span>
            Share your links or QR codes with anyone to receive payments
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[#61CE70] font-bold mt-0.5">5.</span>
            Funds are sent directly to your CashTap wallet
          </li>
        </ul>
      </div>
    </div>
  );
}
